import React from "react"
import { FormProvider, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { IconSize } from "shared/types/Icon"
import { SelectOption } from "shared/types/Select"
import { TeamMember } from "shared/types/__generated__/graphql"
import { ButtonVariant } from "shared/ui/buttons/Button"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import { AdminTeamLeaveFormData } from "../model/useTeamLeaveOverlayModel"
import { AdminTeamLeaveStepIndicator } from "./AdminTeamLeaveStepIndicator"
import { TeamMemberInput } from "./team-member-input/TeamMemberInput"

export interface AdminTeamLeaveOverlayViewProps {
  form: UseFormReturn<AdminTeamLeaveFormData>
  teamMemberOptions: SelectOption<string, TeamMember>[]
  hasNoCandidates: boolean
  wasLastAdmin: boolean
  onSubmit: (ev: React.FormEvent) => Promise<void> | void
  onCancel: () => void
}

export const AdminTeamLeaveOverlayView = React.memo(
  ({
    form,
    teamMemberOptions,
    hasNoCandidates,
    wasLastAdmin,
    onSubmit,
    onCancel
  }: AdminTeamLeaveOverlayViewProps) => {
    const { t } = useTranslation()

    return (
      <FormOverlay
        id="team-leave-overlay__new-admin-assign"
        form="assign-admin-and-team-leave-overlay-form"
        className="team-leave-overlay"
        headline={t("Team.Leave.AreYouSure")}
        submitButtonText={t("Team.AssignNewAdminAndContinue")}
        cancelButtonText={t("General.Cancel")}
        submitButtonProps={{
          className: "team-leave-actions__submit"
        }}
        cancelButtonProps={{
          variant: ButtonVariant.TERTIARY,
          className: "team-leave-actions__cancel"
        }}
        isSubmitDisabled={hasNoCandidates}
        showCloseButton
        onCancel={onCancel}
      >
        <FormProvider {...form}>
          <form
            id="assign-admin-and-team-leave-overlay-form"
            className="overlay-form"
            onSubmit={onSubmit}
          >
            <AdminTeamLeaveStepIndicator wasLastAdmin={wasLastAdmin} />

            <div className="team-leave-overlay__hint">
              <Hint.Root data-testid="team-leave-last-admin-hint">
                <Hint.Icon name={IconName.INFO} size={IconSize.XS} />
                <Hint.Content>
                  <Hint.Title>
                    {t("Team.Leave.AsLastAdmin.WarningTitle")}
                  </Hint.Title>
                  <Hint.Text>
                    {t("Team.Leave.AsLastAdmin.WarningText")}
                  </Hint.Text>
                </Hint.Content>
              </Hint.Root>
            </div>

            <div className="team-leave-overlay__admin-select-input mt-2">
              <TeamMemberInput
                options={teamMemberOptions}
                loading={false}
                disabled={hasNoCandidates}
              />
            </div>

            {hasNoCandidates && (
              <div className="team-leave-overlay__hint mt-2">
                <Hint.Root variant="warning">
                  <Hint.Icon name={IconName.INFO} size={IconSize.XS} />
                  <Hint.Content>
                    <Hint.Title>
                      {t("Team.Leave.AsLastAdmin.NoCandidatesTitle")}
                    </Hint.Title>
                    <Hint.Text>
                      {t("Team.Leave.AsLastAdmin.NoCandidatesText")}
                    </Hint.Text>
                  </Hint.Content>
                </Hint.Root>
              </div>
            )}
          </form>
        </FormProvider>
      </FormOverlay>
    )
  }
)
